import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { LegacyTablerIconSize } from '#pplx/frontend/aether/primitives/IconPrimitive/iconSizing.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { PopoverPrimitive } from '#pplx/frontend/aether/primitives/PopoverPrimitive/index.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { SuggestionGroup } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/components/SuggestCategories.js';
import { CATEGORY_CONFIG, getSortedCategories, groupSuggestionsByCategory, } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/components/SuggestCategoriesShared.js';
import { SuggestionRightAccessory } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/components/SuggestionRightAccessory.js';
import { useFillSuggestion } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/hooks/useFillSuggestion.js';
import { useSuggestionRowIcon } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/hooks/useSuggestionRowIcon.js';
import { getDisplayedSuggestionDescription, isFinanceRootUrl, isNonSequentialMatch, splitIntoInputsAndCompletions, } from '#pplx/frontend/apps/ask/suggest-dropdown/primitives/utils/utils.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { CommandPanel } from '#pplx/frontend/composer-sdk/components/CommandPanel.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
const DefaultLeftAccessory = React.memo(function DefaultLeftAccessory({ image: imageProp, alt, url, iconName, value, category, }) {
    const [isBrokenImage, setIsBrokenImage] = useState(false);
    const getRowIcon = useSuggestionRowIcon();
    const { image, icon } = getRowIcon({ image: imageProp, url, icon: iconName, category }, value);
    if (category === 'shortcuts' && !iconName) {
        return (_jsx(IconPrimitive, { icon: icon, size: LegacyTablerIconSize.sm, className: "mt-two mr-2 w-5 shrink-0" }));
    }
    if (isFinanceRootUrl(url)) {
        return (_jsx("div", { className: "mr-2 flex size-5 shrink-0 items-center justify-center", children: _jsx(Icon, { icon: icon, size: "small", color: "default" }) }));
    }
    return image && !isBrokenImage ? (_jsx("div", { className: "relative mr-2 size-5 shrink-0 overflow-hidden rounded-xs", children: _jsx("img", { src: image, alt: alt ?? '', className: "object-cover", sizes: "24px", onError: () => setIsBrokenImage(true) }) })) : (_jsx("div", { className: "flex shrink-0 items-center mr-2 w-5", children: _jsx(Icon, { icon: isBrokenImage && !!url ? $icon('world') : icon, size: "small" }) }));
});
export const SuggestDropdown = React.memo(function SuggestDropdown({ isOpen, composerAppearance, suggestedQueries, focusedIndex, setFocusedIndex, handlePasteQuery, handleSubmit, isDashedStroke = false, dropdownClassName, dropdownType = 'query', renderQueryContent, renderSuggestionItem, value, children, popoverClassName, categoryTitleClassName, userInputQuery, portalTarget, placement = 'bottom', allowNonSequentialMatch = false, renderSettingsRow, scrollableShards, lockScroll = false, shouldEnableStacking = false, matchTriggerWidthInset, offsetPx = 0, searchModeOverride, }) {
    const threadSearchMode = useSelectedSearchMode();
    const selectedSearchMode = searchModeOverride ?? threadSearchMode;
    const inputsAndCompletions = useMemo(() => {
        return suggestedQueries.map(({ query: suggestionQuery, query_preview, query_uuid, image, imageDark, url, title, description = '', identifier = '', category, subscribed = false, metadata, submission_query, icon, shouldShowTitle, actionHandler, isPersonalized, isPromo, isLearnAboutMe, focus, }, index, array) => {
            const displayQuery = query_preview ?? suggestionQuery;
            if (dropdownType === 'select') {
                return {
                    input: displayQuery,
                    completion: '',
                    query_uuid,
                    title,
                    image,
                    imageDark,
                    url,
                    description,
                    identifier,
                    category,
                    subscribed,
                    submission_query,
                    icon_name: icon,
                    shouldShowTitle,
                    actionHandler,
                    isPersonalized,
                    isPromo,
                    isLearnAboutMe,
                };
            }
            const queryValue = userInputQuery ? (focusedIndex === -1 ? value : userInputQuery) : value;
            const displayedDescription = getDisplayedSuggestionDescription({
                description,
                category,
                focus,
            });
            if (queryValue.length > 0 && suggestionQuery.startsWith(queryValue)) {
                return {
                    input: queryValue,
                    completion: suggestionQuery.slice(queryValue.length),
                    query_uuid,
                    title,
                    image,
                    imageDark,
                    url,
                    category,
                    metadata,
                    ...(displayedDescription !== undefined && {
                        description: displayedDescription,
                    }),
                    submission_query,
                    icon_name: icon,
                    shouldShowTitle,
                    actionHandler,
                    isPersonalized,
                    isPromo,
                    isLearnAboutMe,
                };
            }
            else if (allowNonSequentialMatch &&
                !description &&
                queryValue.length > 0 &&
                queryValue.length <= suggestionQuery.length &&
                array[index] &&
                isNonSequentialMatch(queryValue, array[index], true)[0]) {
                return {
                    input: '',
                    completion: suggestionQuery,
                    inputs_and_completions: splitIntoInputsAndCompletions(queryValue, suggestionQuery),
                    query_uuid,
                    title,
                    image,
                    url,
                    category,
                    metadata,
                    ...(displayedDescription !== undefined && {
                        description: displayedDescription,
                    }),
                    submission_query,
                    icon_name: icon,
                    shouldShowTitle,
                    actionHandler,
                    isPersonalized,
                    isPromo,
                    isLearnAboutMe,
                };
            }
            return {
                input: selectedSearchMode === SearchMode.SEARCH && submission_query
                    ? submission_query
                    : displayQuery,
                completion: '',
                query_uuid,
                title,
                image,
                imageDark,
                url,
                category,
                metadata,
                ...(displayedDescription !== undefined && {
                    description: displayedDescription,
                }),
                submission_query,
                icon_name: icon,
                shouldShowTitle,
                actionHandler,
                isPersonalized,
                isPromo,
                isLearnAboutMe,
            };
        });
    }, [
        suggestedQueries,
        value,
        dropdownType,
        userInputQuery,
        focusedIndex,
        allowNonSequentialMatch,
        selectedSearchMode,
    ]);
    const groupedSuggestions = useMemo(() => {
        return groupSuggestionsByCategory(inputsAndCompletions);
    }, [inputsAndCompletions]);
    const sortedCategories = useMemo(() => {
        return getSortedCategories(groupedSuggestions);
    }, [groupedSuggestions]);
    const onFillButtonClick = useFillSuggestion(handlePasteQuery);
    const settingsRow = renderSettingsRow?.();
    const hasSuggestions = suggestedQueries.length > 0;
    const popoverClasses = classNames('!block w-full', popoverClassName);
    const borderRadius = placement === 'bottom' ? 'rounded-b-2xl' : 'rounded-t-2xl';
    const contentRef = useRef(null);
    // Only show dropdown if open and there are suggestions
    const shouldShowDropdown = isOpen && suggestedQueries.length > 0;
    const { portalTargetElement: contextPortalTarget } = useAetherContext();
    // Open state is fully owned by the parent (input focus/blur/keys), so Radix
    // open-change requests (outside click, Escape) are intentionally ignored,
    // matching the legacy Popover's controlled behavior.
    const handleOpenChange = useCallback(() => { }, []);
    const handleTriggerMouseEvent = useCallback((e) => {
        // Keep mouse events on the input from reaching parent handlers (legacy
        // Popover trigger behavior that callers rely on).
        e.stopPropagation();
    }, []);
    const content = useMemo(() => (_jsxs(CommandPanel, { variant: "suggestDropdown", placement: placement, composerAppearance: composerAppearance, hasContent: hasSuggestions, isDashedStroke: isDashedStroke, className: dropdownClassName, children: [placement === 'top' && settingsRow, shouldShowDropdown && (_jsx("div", { ref: contentRef, className: "flex flex-col items-stretch", "data-testid": "autosuggestion-container", children: sortedCategories.map((categoryKey, categoryIndex) => {
                    const suggestions = groupedSuggestions[categoryKey];
                    const config = CATEGORY_CONFIG[categoryKey] || {
                        order: 999,
                        showHeader: false,
                    };
                    let indexOffset = 0;
                    for (let i = 0; i < categoryIndex; i++) {
                        const prevCategoryKey = sortedCategories[i] ?? '';
                        indexOffset += groupedSuggestions[prevCategoryKey]?.length || 0;
                    }
                    if (!suggestions?.length)
                        return null;
                    return (_jsx(SuggestionGroup, { category: categoryKey, suggestions: suggestions || [], categoryConfig: config, value: value, focusedIndex: focusedIndex, setFocusedIndex: setFocusedIndex, handleSubmit: handleSubmit, indexOffset: indexOffset, LeftAccessoryComponent: DefaultLeftAccessory, RightAccessoryComponent: SuggestionRightAccessory, renderQueryContent: renderQueryContent, renderSuggestionItem: renderSuggestionItem, onFillButtonClick: onFillButtonClick, titleClassName: categoryTitleClassName }, categoryKey));
                }) })), placement === 'bottom' && settingsRow] })), [
        placement,
        hasSuggestions,
        isDashedStroke,
        dropdownClassName,
        settingsRow,
        shouldShowDropdown,
        composerAppearance,
        sortedCategories,
        groupedSuggestions,
        value,
        focusedIndex,
        setFocusedIndex,
        handleSubmit,
        renderQueryContent,
        renderSuggestionItem,
        onFillButtonClick,
        categoryTitleClassName,
    ]);
    const contentSlotProps = useMemo(() => ({
        className: classNames('bg-base min-h-0 min-w-0', { 'z-[1]': shouldEnableStacking }, borderRadius, popoverClasses),
        style: {
            width: matchTriggerWidthInset
                ? `calc(var(--radix-popover-trigger-width) - ${matchTriggerWidthInset})`
                : 'var(--radix-popover-trigger-width)',
        },
    }), [shouldEnableStacking, borderRadius, popoverClasses, matchTriggerWidthInset]);
    return (_jsx(PopoverPrimitive, { open: isOpen, onOpenChange: handleOpenChange, side: placement, align: "center", 
        // Meet the input exactly: overlapping the two clipped shadows darkens the join.
        offsetPx: offsetPx, matchTriggerWidth: true, matchTriggerWidthInset: matchTriggerWidthInset, avoidCollisions: false, shouldAutoFocus: false, shouldUseScrollArea: false, portalContainer: portalTarget ?? contextPortalTarget ?? undefined, triggerElement: _jsx("span", { className: classNames('grow block', {
                'relative z-10': isOpen,
            }), onMouseDown: handleTriggerMouseEvent, onClick: handleTriggerMouseEvent, children: children }), contentSlotProps: contentSlotProps, children: _jsx(RemoveScroll, { enabled: lockScroll, removeScrollBar: false, allowPinchZoom: true, shards: scrollableShards, children: content }) }));
});
//# sourceMappingURL=SuggestDropdown.js.map